import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { EmptyState } from './EmptyState';
import { Icon } from './Icon';
import { useT } from '../../i18n';
import { COMMON } from '../../i18n/common';

export interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  /** `''` — причина не названа (подпись берётся из каталога при рендере). */
  message: string | null;
}

function reload(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

/**
 * Последний рубеж экрана: тело страницы падает во время рендера — раздел
 * станции показывает карточку, а не всё приложение белым экраном. Тот же
 * приём, что у `map/MapErrorBoundary` для кадра карты, только на уровне
 * оболочки целиком: падает один маршрут, сайдбар и шапка остаются живы.
 *
 * Только класс-компонент умеет `getDerivedStateFromError` — и это тот метод,
 * что отрабатывает и в браузере, и при серверном рендере (SSR/смоук):
 * `componentDidCatch` — коммит-фаза, на сервере её не бывает.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { message: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const message =
      error instanceof Error && error.message
        ? error.message
        : typeof error === 'string' && error
          ? error
          : '';
    return { message };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Логируем в dev: в проде прототипа консоль не шумит, причина уже на экране.
    if (import.meta.env.DEV) {
      console.warn('[shell] экран не отрисован:', error, info.componentStack);
    }
  }

  render(): ReactNode {
    const { message } = this.state;
    if (message === null) return this.props.children;

    return <RenderFailed message={message} />;
  }
}

/**
 * Карточка упавшего экрана — функцией, а не в `render` класса: строкам нужен
 * язык интерфейса (`useT`), а хуки живут только в функциях.
 */
function RenderFailed({ message }: { message: string }) {
  const t = useT();
  return (
    <div role="alert">
      <EmptyState
        icon={AlertTriangle}
        title={t(COMMON.renderFailedTitle, { message: message || t(COMMON.noReason) })}
        reason={t(COMMON.renderFailedReason)}
        action={
          <button type="button" className="btn btn--secondary focus-ring" onClick={reload}>
            <Icon icon={RotateCcw} size={18} />
            {t(COMMON.reload)}
          </button>
        }
      />
    </div>
  );
}
